<template>
  <div>
    <div v-if="!isExpanded">
      <Input type="text" placeholder="Add a task..." class="bg-white cursor-pointer" @focus="expand" />
    </div>

    <div v-else class="border border-gray-200 rounded-lg p-3 space-y-3 bg-gray-50/30">
      <Input ref="taskNameInput" v-model="taskName" type="text" placeholder="Task description" class="bg-white" />

      <Select v-model="assigneeBind">
        <SelectTrigger class="bg-white">
          <SelectValue :placeholder="assigneeBind || 'Select assignee'" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="option in assigneeOptionsList" :key="option" :value="option">
            {{ option }}
          </SelectItem>
        </SelectContent>
      </Select>

      <button
        v-if="!showDatePicker"
        type="button"
        class="w-full text-left px-3 py-2 text-sm text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 bg-white"
        @click="showDatePickerAndOpen"
      >
        + Add due date
      </button>
      <DatePicker v-else ref="datePickerRef" v-model="dueDateString" disable-today-and-past />

      <div class="flex gap-2">
        <Button class="flex-1" :disabled="isSubmitting" @click="emit('action')"> {{ actionTitle }} </Button>
        <Button class="flex-1" variant="outline" @click="handleCancel"> Cancel </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import type { UseOneOffTaskInputReturn } from './useOneOffTaskInput';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date_picker';

const props = defineProps<{
  controller: UseOneOffTaskInputReturn;
  actionTitle: string;
}>();

const emit = defineEmits<{
  action: [];
  cancel: [];
}>();

const {
  isExpanded,
  isSubmitting,
  showDatePicker,
  taskName,
  dueDateString,
  assigneeBind,
  assigneeOptions,
  inputEl,
  expand,
  reset,
} = props.controller;

const taskNameInput = ref<InstanceType<typeof Input> | null>(null);
const datePickerRef = ref<InstanceType<typeof DatePicker> | null>(null);

const showDatePickerAndOpen = async () => {
  showDatePicker.value = true;
  await nextTick();
  datePickerRef.value?.open();
};
const assigneeOptionsList = computed(() => assigneeOptions());

watch(taskNameInput, comp => {
  inputEl.value = (comp?.$el as HTMLElement) ?? null;
});

const handleCancel = () => {
  reset();
  emit('cancel');
};
</script>
